<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import MainDirections from '@/components/MainDirections.vue'
import ProjectCards from '@/components/ProjectCards.vue'
import ImageGallery from '@/components/ImageGallery.vue'
import PageContainer from '@/components/PageContainer.vue'
import FormModal from '@/components/FormModal.vue'
import RequestButton from '@/components/button/RequestButton.vue'
import { api } from '@/api/api.js'
import { getOptimizedImageUrl } from '@/composables/useOptimizedImage'
import type { Project, BackstageImage } from '@/types/api'

const showModal = ref(false)
const projects = ref<Project[]>([])
const isLoading = ref(false)
const error = ref<string | null>(null)
const backstageImages = ref<{ url: string }[]>([])

const defaultBackstageImages: { url: string }[] = [
  { url: '/static/img/backstage_1.webp' },
  { url: '/static/img/backstage_2.webp' },
  { url: '/static/img/backstage_3.webp' },
  { url: '/static/img/backstage_4.webp' },
]

const fetchBackstageGallery = async (): Promise<void> => {
  try {
    const data = (await api.getBackstageGallery()) as
      | BackstageImage[]
      | { results?: BackstageImage[]; items?: BackstageImage[]; images?: BackstageImage[] }
    const raw: BackstageImage[] =
      (Array.isArray(data) ? data : (data?.results ?? data?.items ?? data?.images ?? [])) || []
    if (raw.length) {
      const mapped = raw
        .map((item: BackstageImage | (BackstageImage & { src?: string; image?: string })) => {
          const rawUrl =
            typeof item === 'string'
              ? item
              : (item.url ??
                (item as any).image_url ??
                (item as any).src ??
                (item as any).image ??
                '')
          if (!rawUrl) return null
          const url = getOptimizedImageUrl(rawUrl, { width: 1200, quality: 85 })
          return { url }
        })
        .filter((v): v is { url: string } => Boolean(v && v.url))
      backstageImages.value = mapped
    }
  } catch (err) {
    console.error('Ошибка загрузки галереи бэкстейдж:', err)
  }
}

const galleryImages = computed<{ url: string }[]>(() =>
  backstageImages.value.length > 0 ? backstageImages.value : defaultBackstageImages,
)

const fetchProjects = async (): Promise<void> => {
  isLoading.value = true
  error.value = null

  try {
    const result = await api.getAllProjects()

    // Проверяем, что результат - это массив
    if (Array.isArray(result)) {
      projects.value = result as Project[]
    } else {
      projects.value = []
    }
  } catch (err) {
    console.error('Ошибка при получении проектов:', err)
    // В production build показываем более мягкое сообщение
    if (!(import.meta as any).env.DEV) {
      error.value = 'Проекты временно недоступны. Проверьте подключение к интернету.'
    } else {
      const message = err instanceof Error ? err.message : String(err)
      error.value = `Не удалось загрузить проекты: ${message}`
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchProjects()
  fetchBackstageGallery()
})
</script>

<template>
  <div class="main-view">
    <PageContainer>
      <div id="block1" class="block">
        <MainDirections />
      </div>

      <div id="block2" class="block">
        <div class="flex flex-center">
          <h1 class="text-xxl text-600 text-center w-100 hero-title">CHAPTER FOUR</h1>
          <p class="text-sm text-center text-300 description">
            Полноценный видеопродакшн<br />Реклама, трансляции, арт.
          </p>
          <div class="form-container">
            <RequestButton text="ОСТАВИТЬ ЗАЯВКУ" @click="showModal = true" />
          </div>
        </div>
      </div>

      <div id="block3" class="block">
        <div class="flex flex-center">
          <p class="text-lg text-left w-100 projects-title">ПОСЛЕДНИЕ ПРОЕКТЫ</p>

          <!-- Состояние ошибки -->
          <div v-if="error" class="error-projects">
            <p>{{ error }}</p>
            <button @click="fetchProjects" class="retry-button">Попробовать снова</button>
          </div>

          <!-- Проекты -->
          <ProjectCards v-else-if="isLoading || projects.length > 0" :projects="projects" />

          <!-- Нет проектов -->
          <div v-else-if="!isLoading" class="no-projects">
            <p>Проекты не найдены</p>
          </div>
        </div>
        <ImageGallery :images="galleryImages" :clip-edges="true" />
      </div>

      <div id="block4" class="block">
        <div class="flex flex-center">
          <p class="text-lg text-left w-100 telegram-title">TELEGRAM</p>
          <a
            href="https://t.me/chapterf444"
            class="text-md text-left link"
            target="_blank"
            rel="noopener noreferrer"
          >
            @chapterf444
          </a>
        </div>
      </div>
      <Transition name="popup-fade" appear>
        <FormModal v-if="showModal" @close="showModal = false" />
      </Transition>
    </PageContainer>
  </div>
</template>

<style scoped>
.main-view {
  width: 100%;
}

.link {
  margin-right: auto;
  margin-bottom: 2rem;
  padding-bottom: 0.25rem;
  background-image: linear-gradient(
    to bottom,
    transparent 0%,
    transparent 95%,
    black 95%,
    black 98%
  );

  background-repeat: no-repeat;
  background-size: 0 100%;
  background-position-x: right;

  transition: background-size 0.5s ease-in-out;
}

.link:hover {
  background-size: 100% 100%;
  background-position-x: left;
}

.block {
  padding: 3.75rem 0;
}

#block1,
#block2 {
  min-height: auto !important;
}

#block4 {
  padding-bottom: 5rem;
}

.hero-title {
  margin-bottom: 0.5rem;
}

.description {
  margin: 1.25rem 0;
  user-select: none;
}

.projects-title {
  font-weight: 400;
  user-select: none;
}

.telegram-title {
  font-weight: 400;
  margin-bottom: 1rem;
  user-select: none;
}

.form-container {
  display: flex;
  justify-content: center;
  margin: 1.25rem auto;
  max-width: 18.75rem;
  width: 100%;
}

.loading-projects,
.error-projects,
.no-projects {
  text-align: center;
  padding: 6.25rem 1.25rem;
  width: 100%;
}

.loading-projects {
  display: flex;
  justify-content: center;
  align-items: center;
}

.error-projects {
  text-align: center;
  padding: 2.5rem 1.25rem;
  width: 100%;
}

.error-projects p {
  color: #e74c3c;
  font-size: 1.1rem;
  margin-bottom: 1.25rem;
}

.error-projects .retry-button {
  display: inline-block;
  margin: 0 0.625rem 0.625rem 0.625rem;
}

.retry-button {
  background: var(--color-primary);
  color: var(--color-white);
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease;
  max-width: 17.5rem;
  margin: 0 auto;
  font-weight: 500;
}

.retry-button:hover {
  background: var(--color-gray-800);
}

.no-projects p {
  color: var(--color-text-muted);
  font-size: 1.1rem;
}

@media (max-width: 64rem) {
  .block {
    padding: 3.125rem 0;
  }
}

@media (max-width: 48rem) {
  .block {
    padding: 2.5rem 0;
  }
}

@media (max-width: 36rem) {
  .block {
    padding: 0 0 1.875rem 0;
  }
}

@media (max-width: 20rem) {
  .block {
    padding: 1.25rem 0;
  }
}
</style>
